import type { Question } from '../../types';
import { HAB_MH_CONDITION } from './shared-options';

// ---------------------------------------------------------------------------
// Builders Risk / Course of Construction Supplement
// Source: Centered Partners MHMF intake — "Builders Risk" sheet.
// Only relevant when expansion, new construction, or major renovation is
// planned during the policy term (pad-site expansion, mini-home build-out,
// unit gut rehab). The prospect opts in by selecting the line.
// ---------------------------------------------------------------------------

const BR_CONDITION = [
  { field: 'coverage_lines', operator: 'contains' as const, value: 'builders_risk' },
  ...HAB_MH_CONDITION,
];

export const buildersRiskQuestions: Question[] = [
  {
    id: 'br_project_type',
    text: 'What type of project is planned?',
    section: 'property',
    group: 'builders_risk',
    sort_order: 700,
    field_type: 'select',
    options: [
      { label: 'New construction', value: 'new_construction' },
      { label: 'Addition / pad-site expansion', value: 'addition' },
      { label: 'Major renovation / gut rehab', value: 'renovation' },
      { label: 'Manufactured home placement', value: 'mh_placement' },
    ],
    required: true,
    field_path: 'builders_risk.project_type',
    conditions: BR_CONDITION,
    tags: ['builders_risk', 'lob'],
  },
  {
    id: 'br_description',
    text: 'Describe the project',
    section: 'property',
    group: 'builders_risk',
    sort_order: 710,
    field_type: 'textarea',
    placeholder: 'Adding 30 pad sites and a new clubhouse at Oak Meadows.',
    required: true,
    field_path: 'builders_risk.description',
    conditions: BR_CONDITION,
    tags: ['builders_risk', 'lob'],
  },
  {
    id: 'br_hard_costs',
    text: 'Hard cost budget (construction value)',
    section: 'property',
    group: 'builders_risk',
    sort_order: 720,
    field_type: 'currency',
    placeholder: '1500000',
    required: true,
    field_path: 'builders_risk.hard_costs',
    conditions: BR_CONDITION,
    tags: ['builders_risk', 'lob'],
  },
  {
    id: 'br_soft_costs',
    text: 'Soft costs to be insured',
    subtext: 'Design, permits, interest carry, lender fees.',
    section: 'property',
    group: 'builders_risk',
    sort_order: 730,
    field_type: 'currency',
    placeholder: '200000',
    required: false,
    field_path: 'builders_risk.soft_costs',
    conditions: BR_CONDITION,
    tags: ['builders_risk', 'lob'],
  },
  {
    id: 'br_start_date',
    text: 'Estimated start date',
    section: 'property',
    group: 'builders_risk',
    sort_order: 740,
    field_type: 'date',
    required: true,
    field_path: 'builders_risk.start_date',
    conditions: BR_CONDITION,
    tags: ['builders_risk', 'lob'],
  },
  {
    id: 'br_completion_date',
    text: 'Estimated completion date',
    section: 'property',
    group: 'builders_risk',
    sort_order: 750,
    field_type: 'date',
    required: true,
    field_path: 'builders_risk.completion_date',
    conditions: BR_CONDITION,
    tags: ['builders_risk', 'lob'],
  },
  {
    id: 'br_gc',
    text: 'Who is the general contractor?',
    section: 'property',
    group: 'builders_risk',
    sort_order: 760,
    field_type: 'text',
    placeholder: 'GC name',
    required: true,
    field_path: 'builders_risk.gc_name',
    conditions: BR_CONDITION,
    tags: ['builders_risk', 'lob'],
  },
  {
    id: 'br_self_performed_pct',
    text: 'What percentage of work is self-performed by the GC (vs subcontracted)?',
    section: 'property',
    group: 'builders_risk',
    sort_order: 770,
    field_type: 'percentage',
    placeholder: '30',
    required: true,
    validation: { min: 0, max: 100 },
    field_path: 'builders_risk.self_performed_pct',
    conditions: BR_CONDITION,
    tags: ['builders_risk', 'lob'],
  },
  {
    id: 'br_sub_insurance',
    text: 'Is there a written subcontractor insurance requirement policy?',
    subtext: 'Additional insured / waiver of subrogation requirements for subs.',
    section: 'property',
    group: 'builders_risk',
    sort_order: 780,
    field_type: 'boolean',
    required: true,
    field_path: 'builders_risk.sub_insurance_policy',
    conditions: BR_CONDITION,
    tags: ['builders_risk', 'lob'],
  },
  {
    id: 'br_foundation',
    text: 'Foundation type and material',
    section: 'property',
    group: 'builders_risk',
    sort_order: 790,
    field_type: 'text',
    placeholder: 'Poured concrete slab',
    required: true,
    field_path: 'builders_risk.foundation',
    conditions: BR_CONDITION,
    tags: ['builders_risk', 'lob'],
  },
  {
    id: 'br_hot_work',
    text: 'Is there a hot work permit policy?',
    subtext: 'Welding, cutting, soldering.',
    section: 'property',
    group: 'builders_risk',
    sort_order: 800,
    field_type: 'boolean',
    required: true,
    field_path: 'builders_risk.hot_work_policy',
    conditions: BR_CONDITION,
    tags: ['builders_risk', 'lob'],
  },
  {
    id: 'br_site_security',
    text: 'Site security during construction',
    subtext: 'Select all that apply.',
    section: 'property',
    group: 'builders_risk',
    sort_order: 810,
    field_type: 'multiselect',
    options: [
      { label: 'Fencing', value: 'fence' },
      { label: 'Lighting', value: 'lighting' },
      { label: 'Cameras', value: 'cameras' },
      { label: 'Guard service', value: 'guard' },
      { label: 'None of these', value: 'none' },
    ],
    required: true,
    field_path: 'builders_risk.site_security',
    conditions: BR_CONDITION,
    tags: ['builders_risk', 'lob'],
  },
  {
    id: 'br_cat_zone',
    text: 'Is the project in a catastrophe zone (wind, hail, flood, wildfire)?',
    section: 'property',
    group: 'builders_risk',
    sort_order: 820,
    field_type: 'boolean',
    required: true,
    field_path: 'builders_risk.cat_zone',
    conditions: BR_CONDITION,
    tags: ['builders_risk', 'lob'],
  },
  {
    id: 'br_lender_requirements',
    text: 'Any lender requirements?',
    subtext: 'Limits, deductibles, named insureds.',
    section: 'property',
    group: 'builders_risk',
    sort_order: 830,
    field_type: 'textarea',
    required: false,
    field_path: 'builders_risk.lender_requirements',
    conditions: BR_CONDITION,
    tags: ['builders_risk', 'lob'],
  },
  {
    id: 'br_delay_startup',
    text: 'Is delay-in-startup / soft cost coverage requested?',
    section: 'property',
    group: 'builders_risk',
    sort_order: 840,
    field_type: 'boolean',
    required: true,
    field_path: 'builders_risk.delay_in_startup',
    conditions: BR_CONDITION,
    tags: ['builders_risk', 'lob'],
  },
  {
    id: 'br_ocip',
    text: 'Is the project owner-controlled (OCIP) or contractor-controlled (CCIP)?',
    section: 'property',
    group: 'builders_risk',
    sort_order: 850,
    field_type: 'select',
    options: [
      { label: 'OCIP — owner-controlled', value: 'ocip' },
      { label: 'CCIP — contractor-controlled', value: 'ccip' },
      { label: 'Neither / traditional', value: 'neither' },
    ],
    required: true,
    field_path: 'builders_risk.ocip_ccip',
    conditions: BR_CONDITION,
    tags: ['builders_risk', 'lob'],
  },
];
